/** @jsx isolateComponent */

import React, { useEffect, useRef } from 'react';
import { css, keyframes } from '@emotion/core';
import { Animation, Colors, Shadows, Zindexes } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { useClickOutside, useIntersect } from '../../../hooks';
import { useTheme } from '../../../index';

type Props = {
  /**
   * The horizontal alignment of the box relative to the parent element.
   * The vertical position is handled automatically by state.
   */
  align?: 'right' | 'left' | 'stretch';

  /**
   * What to render in the box.
   */
  children: React.ReactNode;

  /**
   * Override intersect observer's calculated height for box + children since it
   * isn't always accurate
   */
  intersectHeight?: number;

  /** Optionally set max width of box */
  maxWidth?: string;

  /** Optionally set the min width of the box */
  minWidth?: string;

  /**
   * Optional callback for when a click occurs outside of `FloatingBox`.
   * Typically used to unrender `FloatingBox`.
   */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  /**
   * The vertical alignment of the box relative to the parent element.
   * If position is not specified, it is handled automatically by state.
   */

  position?: 'north' | 'south';

  /**
   * Defaults to 3px in v5, 10px in v4.
   * AccountsMenu is 10px even in v5, so we need a way to override here.
   * Defaults to 3, since v4 will get 10 assigned directly
   * I don't think this will get removed when v5 becomes the standard because
   * AccountsMenu will always have that change from the default behavior.
   * See: https://zapier.slack.com/archives/C026EB5GAKZ/p1638557623356400?thread_ts=1638556718.354700&cid=C026EB5GAKZ
   */
  borderRadius?: number;

  /**
   * Just like borderRadius, this has a default of gray20 in v4 and
   * neutral400 in v5, but AccountsMenu uses neutral300. This means we
   * need a way to configure this.
   * I don't think this will get removed when v5 becomes the standard because
   * AccountsMenu will always have that change from the default behavior.
   * See: https://zapier.slack.com/archives/C026EB5GAKZ/p1638885120477000
   */
  borderColor?: string;
};

type DerivedState = {
  position: 'north' | 'south';
};

const menuOpenAnimation = keyframes({
  '0%': {
    transform: 'translateY(-10px)',
    opacity: 0,
  },
  // This "delay" gives enough time for the menu to be measured
  // by `useIntersect` and flip upwards if it needs to, without
  // flickering for a moment while it renders down and is measured.
  '30%': {
    transform: 'translateY(-10px)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

const DISTANCE = 'calc(100% + 5px)';

const Styles = labelStyles('FloatingBox', {
  root: (props: Props, derivedState: DerivedState) => [
    css({
      animation: `${Animation.transitionDuration} ease-out ${menuOpenAnimation}`,
      maxWidth: props.maxWidth,
      position: 'absolute',
      minWidth: props.minWidth,
      zIndex: Zindexes.dropdownMenu,
    }),
    props.align === 'left' &&
      withModifier(props.align, {
        left: 0,
      }),
    props.align === 'right' &&
      withModifier(props.align, {
        right: 0,
      }),
    props.align === 'stretch' &&
      withModifier(props.align, {
        left: 0,
        right: 0,
      }),
    derivedState.position === 'south' &&
      withModifier(derivedState.position, {
        top: DISTANCE,
      }),
    derivedState.position === 'north' &&
      withModifier(derivedState.position, {
        bottom: DISTANCE,
      }),
  ],
  box: (borderRadius: number, borderColor: string, isRebrand = false) =>
    css({
      backgroundColor: isRebrand ? Colors.neutral100 : Colors.white,
      border: `1px solid ${borderColor}`,
      borderRadius,
      boxShadow: Shadows.elevation20,
    }),
});

/**
 * Since we can't conditionally change FloatingBox.defaultProps, instead
 * we have this function to conditionally return 3 as the default for rebrand,
 * and 10 for original brand. When we make v5 the standard, remove the `isRebrand`
 * conditional and remove the `borderRadius || 10` return statement.
 */
function getBorderRadius(borderRadius: number | undefined, isRebrand = false) {
  if (isRebrand) {
    return borderRadius || 3;
  }

  return borderRadius || 10;
}

/**
 * Same as the above function `getBorderRadius` - this is here because
 * we can't conditionally change FloatingBox.defaultProps. Instead
 * we return Colors.neutral400 for v5 by default, and Colors.gray20 by
 * default for v4. When we make v5 the standard, remove the `isRebrand`
 * Conditional and remove the `borderColor || Colors.gray20` return statement.
 */
function getBorderColor(borderColor: string | undefined, isRebrand = false) {
  if (isRebrand) {
    return borderColor || Colors.neutral400;
  }

  return borderColor || Colors.gray20;
}

/**
 * Box that floats and reorients itself if it renders out of the viewport.
 */
export const FloatingBox = (props: Props) => {
  const { isRebrand } = useTheme();

  const threshold = 0.9;
  const node = useRef<HTMLDivElement>(null);
  const [
    setIntersectNode,
    { isBottomIntersecting, isTopIntersecting },
  ] = useIntersect({
    heightOverride: props.intersectHeight,
    once: true,
    threshold,
  });

  const borderRadius = getBorderRadius(props.borderRadius, isRebrand);
  const borderColor = getBorderColor(props.borderColor, isRebrand);

  const onClickOutside = props.onClickOutside || (() => {});

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside,
  });

  useEffect(() => {
    setIntersectNode(node.current);
    setClickOutsideNode(node.current);
  }, [setClickOutsideNode, setIntersectNode]);

  const derivedState = {
    position:
      props.position ||
      (isTopIntersecting || !isBottomIntersecting ? 'south' : 'north'),
  };

  return (
    <div css={Styles.root(props, derivedState)} ref={node}>
      <div css={Styles.box(borderRadius, borderColor, isRebrand)}>
        {props.children}
      </div>
    </div>
  );
};

FloatingBox.defaultProps = {
  align: 'left',
};
